/* eslint-disable valtio/state-snapshot-rule */
import React, { useEffect, useRef } from 'react';
import { useSnapshot } from 'valtio';

import '../../www/assets/css/color_bar_html.css';
import DataValueRect from '../../www/assets/images/data_value_rect.svg';

import { datareadStore, datasetStore, refsStore } from '../managers/globalState';
import { setReadoutData } from './data_readout';


/**
 * The color values from our color bar  are a one-time calculation, even after unmount and remount
 * so we can store them outside the component
 */
const colorValues = {};

const extractColorData = img => {
	const { currentDataset } = datasetStore.stateSnapshot;
	const { externalId } = currentDataset;

	if (colorValues[externalId]) {
		return colorValues[externalId];
	}

	const canvas = document.createElement('canvas');
	const ctx = canvas.getContext('2d');

	canvas.width = img.width;
	canvas.height = 1;

	ctx.drawImage(img, 0, 0);

	const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

	colorValues[externalId] = [];

	for (let i = 0; i < data.length; i += 4) {
		colorValues[externalId].push(
			[data[i], data[i + 1], data[i + 2]]
		);
	}
};

/**
 * Jeff Hall built an equation to generate a list of values that correctly represented
 * it's matching index to the list of extracted color data values for the precipiitation color bar.
 * Rather than directly copying the values as an array, this function is the javascript equivalent.
 * It runs once on app load, so can be stored outside the components.
 * @param {Boolean} expander
 * @returns {Array<number>}
 */
const generatePrecipitationValues = (expander = 10000) => {
	const gamma = 0.360;
	const minval = 2.5;
	const maxval = 560;
	const ncolors = 236;

	const values = [];

	// Sample in linear increments.
	for (let i = 0; i < ncolors; i++) {
		const val = i * ((maxval - minval) / (ncolors - 1));
		let minDiff = Infinity;
		let minIndex = 0;

		for (let j = 0; j < expander; j++) {
			const diff = Math.abs(Math.pow(j / (expander - 1), gamma) * (maxval - minval) - val);
			if (diff < minDiff) {
				minDiff = diff;
				minIndex = j;
			}
		}

		values[i] = Number((minIndex / (expander - 1) * (maxval - minval) + minval).toFixed(2));
	}

	values.shift();

	return values;
};

// Generate precipitation values (takes ~250ms with expander at 10000)
const dataLookup = generatePrecipitationValues();

/**
 * Using the input value, geet the nearest data value and its index from the dataLookup array.
 * Note: Asssumes dataLookup is numerically sorted.
 * @param {number} dataValue
 * @returns {number}
 */
const getNearestDataIndex = dataValue => {
	let nearestIndex = 0;

	for (const i in dataLookup) {
		if (dataValue > dataLookup[i]) {
			nearestIndex = i;
		} else {
			return nearestIndex;
		}
	}

	return dataLookup.length - 1;
};


/**
 * Calculates the color reactangle style / positioning, specific for precipitation.
 * @param {number|null} nearestIndex
 * @param {boolean} isSnow
 * @param {Pioneer.Engine} pioneer
 */
const setPrecipitationColorRectStyle = (nearestIndex, isSnow, pioneer) => {
	const isValid = nearestIndex !== null;
	let colorRectStyle = null;

	if (isValid) {
		const verticalOffsetValue = isSnow ? 78 : 16;

		const leftValue = nearestIndex;

		colorRectStyle = {
			display: 'block',
			left: `${leftValue.toString()}px`,
			top: `${verticalOffsetValue.toString()}px`
		};
	}

	// Once the rect style is set, we can set an onUpdate callback to detect any cam moves
	// to reset style and dataread.
	const onUpdate = () => {
		const input = pioneer.getInput();
		const { x, y } = input.getDraggedOffset();
		const zoom = input.getZoomedOffset();
		const camMoved = x !== 0 || y !== 0 || zoom !== 0;

		// Reset rect style, readout and remove callback.
		if (camMoved) {
			datareadStore.setGlobalState({ colorRectStyle: null });
			setReadoutData(null);
			pioneer.removeCallback(onUpdate);
		}
	};

	// Add a pioneer callback.
	pioneer.addCallback(onUpdate, true);

	// Set global store.
	datareadStore.setGlobalState({ colorRectStyle });
};

const ColorBarHtml = ({ readoutKey }) => {
	const imgRef = useRef();
	const { get } = useSnapshot(refsStore.state);
	const { dataset } = get('dataset');

	const { readoutData } = dataset;
	const { src, label, markers } = (readoutKey ? readoutData[readoutKey] : readoutData) || {};
	const colorBarSrc = `assets/images/color_bars/${src}`;

	const width = 235;
	const height = 8;

	useEffect(() => {
		// Store ref to variable so we can reference it in the cleanup.
		const imgRefCurrent = imgRef.current;

		if (imgRefCurrent) {
			const getColorData = () => extractColorData(imgRefCurrent);
			imgRefCurrent.addEventListener('load', getColorData);

			return () => imgRefCurrent.removeEventListener('load', getColorData);
		}
	}, [imgRef]);

	return (
		<figure className='color-bar-html-container'>
			<figcaption>{label}</figcaption>
			<img ref={imgRef} src={colorBarSrc} alt={label} width={`${width}px`} height={`${height}px`} />
			<div className='scale'>{markers?.map((marker, i) => <span key={i}>{marker}</span>)}</div>
		</figure>

	);
};

const PrecipitationColorBar = ({ showDataRect = true }) => {
	const { colorRectStyle } = useSnapshot(datareadStore.state);
	const { get } = useSnapshot(refsStore.state);
	const { dataset } = get('dataset');

	const { rain, snow } = dataset?.readoutData || {};

	if (!rain || !snow) {
		return null;
	}

	return (
		<div className='precipitation-color-bar-container'>
			<ColorBarHtml readoutKey='rain' />
			{ showDataRect && <span style={colorRectStyle}><DataValueRect /></span> }
			<ColorBarHtml readoutKey='snow' />
		</div>
	);
};

export default ColorBarHtml;
export { dataLookup, getNearestDataIndex, setPrecipitationColorRectStyle, PrecipitationColorBar };
